/*!
 * PP Cart Samples v2 — cart drawer sample picker handler
 * Wires [data-sample-btn] tiles in snippets/cart-free-samples.liquid to
 * /cart/add.js and /cart/change.js. Max 2 samples. Relies on Pipeline's
 * theme:cart:reload event to refresh drawer HTML (server-rendered is-selected
 * state is the source of truth). Pricing lives on the two automatic BxGy
 * discounts ("Free Sample with Purchase" ×1 and "2 Free Samples with
 * Purchase" ×2, combinable), so 1 OR 2 picked samples are both free.
 *
 * v2 (2026-07-02):
 * - remove targets the LINE KEY: /cart/change.js rejects a numeric variant id
 *   with 400 ("expected Integer to be \d+ or \d+:hash"), so unselect never
 *   worked — resolve the line via /cart.js first.
 * - picker-added lines carry a hidden "_pp_sample" property so they can be
 *   told apart from a deliberately purchased 2ml.
 * - auto-heal: when no qualifying full-size remains in the cart, picker-added
 *   samples are removed instead of silently turning into paid €15 lines; a
 *   picker line bumped above qty 1 is clamped back.
 * - theme:cart:change is dispatched WITH {detail:{cart}} — Pipeline's
 *   listeners read e.detail.cart and threw on the bare event v1 sent.
 *
 * v3 (2026-10-30, Pierre « i don't see my sample selector for free samples
 * when i buy bottle »): the picker is back under the cart lines (drawer and
 * /cart). A line qualifies only when it is a 50 or 90 ml bottle (SKU PF…45 /
 * PF…90), the same 14 variants the two discounts buy on; any other product
 * no longer keeps picker samples in the cart as paid lines.
 */
(function () {
  'use strict';

  var MAX_SAMPLES = 2;
  var BTN_SEL = '[data-sample-btn]';
  var CONTAINER_SEL = '[data-cart-samples]';
  var SAMPLE_PROP = '_pp_sample';
  var inFlight = false;
  var healing = false;

  function jsonHeaders() {
    return { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    });
  }

  function changeByKey(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ id: String(key), quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('change failed');
      return r.json();
    });
  }

  function addSample(variantId) {
    var props = {};
    props[SAMPLE_PROP] = '1';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return { description: 'add failed' }; })
          .then(function (e) { throw new Error(e.description || 'add failed'); });
      }
      return r.json();
    });
  }

  function removeSample(variantId) {
    var vid = String(variantId);
    return getCart().then(function (cart) {
      var line = null;
      var i;
      // prefer the picker-added line; fall back to any line of that variant
      for (i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (String(it.variant_id) !== vid) continue;
        if (it.properties && it.properties[SAMPLE_PROP]) { line = it; break; }
        if (!line) line = it;
      }
      if (!line) return cart;
      return changeByKey(line.key, 0);
    });
  }

  function reloadDrawer() {
    document.dispatchEvent(new CustomEvent('theme:cart:reload'));
    getCart().then(function (cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
    }).catch(function () { /* count refresh is cosmetic; drawer already reloading */ });
  }

  function isSampleLine(item) {
    return !!(item.properties && item.properties[SAMPLE_PROP]);
  }

  function isQualifyingLine(item) {
    if (isSampleLine(item)) return false;
    return /^(PF[A-Z]+(45|90)|RT[A-Z]{4}(SMALL|LARGE)G0[12])$/.test((item.sku || '').toUpperCase());
  }

  /* Free samples only exist alongside a full-size purchase. If the bottle
     leaves the cart, drop the picker-added samples (the BxGy discount stops
     applying and they'd become paid lines); clamp picker lines back to qty 1. */
  function heal(cart) {
    if (healing || inFlight) return;
    var samples = cart.items.filter(isSampleLine);
    if (!samples.length) return;
    var qualifying = cart.items.some(isQualifyingLine);
    var ops = [];
    if (!qualifying) {
      ops = samples.map(function (s) { return { key: s.key, quantity: 0 }; });
    } else {
      ops = samples.filter(function (s) { return s.quantity > 1; })
        .map(function (s) { return { key: s.key, quantity: 1 }; });
    }
    if (!ops.length) return;
    healing = true;
    ops.reduce(function (p, op) {
      return p.then(function () { return changeByKey(op.key, op.quantity); });
    }, Promise.resolve())
      .then(function () { reloadDrawer(); })
      .catch(function (err) { console.error('[cart-samples] heal', err); })
      .finally(function () { healing = false; });
  }

  function handleClick(btn) {
    if (inFlight) return;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var container = btn.closest(CONTAINER_SEL);
    var selectedCount = container
      ? container.querySelectorAll(BTN_SEL + '.is-selected').length
      : 0;
    var isSelected = btn.classList.contains('is-selected');

    if (!isSelected && selectedCount >= MAX_SAMPLES) {
      btn.classList.add('is-disabled');
      setTimeout(function () { btn.classList.remove('is-disabled'); }, 500);
      return;
    }

    inFlight = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var action = isSelected ? removeSample(variantId) : addSample(variantId);
    action
      .then(function () { reloadDrawer(); })
      .catch(function (err) {
        console.error('[cart-samples]', err);
        btn.classList.add('is-error');
        setTimeout(function () { btn.classList.remove('is-error'); }, 800);
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        inFlight = false;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest(BTN_SEL);
    if (!btn) return;
    e.preventDefault();
    handleClick(btn);
  });

  document.addEventListener('theme:cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && cart.items) heal(cart);
  });

  // Initial pass for carts landing on any page (incl. /cart) with a dead bottle.
  // Skip the fetch when the header count already says the cart is empty.
  (function initialHeal() {
    var run = function () {
      var el = document.querySelector('[data-header-cart-count]');
      if (el && (el.textContent || '').replace(/\D/g, '') === '0') return;
      getCart().then(heal).catch(function () { /* offline/etc — nothing to heal */ });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  })();
})();

/* Cart sheet and the keyboard (audit 30/10): the sheet says aria-modal, yet Tab walked out of it into the page behind,
   and Escape left focus on <body>. While it is open, Tab and Shift+Tab go round inside it; when it closes, focus goes
   back to what opened it. */
(function () {
  'use strict';
  var drawer = document.getElementById('drawer-cart');
  if (!drawer) return;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var opener = null;

  function isOpen() {
    return drawer.classList.contains('is-open') || drawer.classList.contains('drawer--visible');
  }

  function focusables() {
    return Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
    });
  }

  // remember the last thing focused outside the sheet while it is closed: that is what opened it
  document.addEventListener('focusin', function (e) {
    if (!isOpen() && !drawer.contains(e.target)) opener = e.target;
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !isOpen()) return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1], active = document.activeElement;
    if (!drawer.contains(active)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  });

  var wasOpen = isOpen();
  new MutationObserver(function () {
    var open = isOpen();
    if (wasOpen && !open && opener && document.contains(opener)) {
      var a = document.activeElement;
      if (!a || a === document.body || drawer.contains(a)) {
        try { opener.focus({ preventScroll: true }); } catch (err) { /* opener gone or not focusable */ }
      }
    }
    wasOpen = open;
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
})();
